import { stringify } from 'querystring';
import firebase from '../../firebase-integration/index';

import { IIndividualProfile, ILeaderProfile } from '../interfaces';
import { searchTerms } from '../utils';

const db = firebase.firestore();
const userCollection = db.collection('users');
const groupCollection = db.collection('groups');
const libraryCollection = db.collection('library');

export const getUserProfileData =
  async (userId: string): Promise<IIndividualProfile | ILeaderProfile | undefined> => {
    if (!userId) {
      throw new Error('No User Id was provided');
    }

    try {
      const userProfile = await userCollection.doc(userId).get();
      return userProfile.data() as IIndividualProfile | ILeaderProfile | undefined;
    } catch (error) {
      throw error;
    }
  };

export const setupUserProfileData = (user: IIndividualProfile) => {
  if (!user.uid) {
    throw new Error('Error Saving User Profile');
  }

  try {
    return userCollection.doc(user.uid).set({
      searchTerms: [
        ...searchTerms([user.displayName, user.firstName, user.lastName]),
      ],
      displayName: user.displayName,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      contactEmail: user.email || '',
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      updeatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      timezone: user.timezone || '',
      referal: user.referal || '',
      agreeTermsConditions: user.agreeTermsConditions || '',
      userType: 'user',
    });
  } catch (error) {
    throw error;
  }
};

export const updateUserProfileData = (user: IIndividualProfile) => {
  if (!user.uid) {
    throw new Error('Error Saving User Profile');
  }

  return userCollection.doc(user.uid).update({
    searchTerms: [
      ...searchTerms([user.displayName, user.firstName, user.lastName]),
    ],
    displayName: user.displayName,
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    email: user.email || '',
    contactEmail: user.contactEmail || user.email || '',
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    timezone: user.timezone || '',
    referal: user.referal || '',
    agreeTermsConditions: user.agreeTermsConditions || '',
    photoUrl: user.photoUrl || '',
    userType: 'user',
  });
};

export const setupLeaderProfileData = (user: ILeaderProfile) => {
  if (!user.uid) {
    throw new Error('Error Saving Leader Profile');
  }

  try {
    return userCollection.doc(user.uid).set({
      searchTerms: [
        ...searchTerms([user.displayName, user.leaderName]),
      ],
      displayName: user.displayName,
      email: user.email || '',
      contactEmail: user.email || '',
      leaderName: user.leaderName || '',
      adminName: user.adminName || '',
      leaderType: user.leaderType || '',
      timezone: user.timezone || '',
      referal: user.referal || '',
      agreeTermsConditions: user.agreeTermsConditions || '',
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      userType: 'leader',
    });
  } catch (error) {
    throw error;
  }
};

export const updateLeaderProfileData = (user: ILeaderProfile) => {
  if (!user.uid) {
    throw new Error('Error Saving Leader Profile');
  }

  try {
    return userCollection.doc(user.uid).update({
      searchTerms: [
        ...searchTerms([user.displayName, user.leaderName]),
      ],
      displayName: user.displayName,
      email: user.email || '',
      contactEmail: user.contactEmail || user.email || '',
      leaderName: user.leaderName || '',
      adminName: user.adminName || '',
      leaderType: user.leaderType || '',
      timezone: user.timezone || '',
      referal: user.referal || '',
      agreeTermsConditions: user.agreeTermsConditions || '',
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      userType: 'leader',
    });
  } catch (error) {
    throw error;
  }
};

export const startFollowing = (userId: string, data: any): Promise<void> => {
  if (!userId) {
    throw new Error('No User Id provided to set following');
  }
  console.log(data);

  return userCollection.doc(userId)
    .collection('following')
    .doc(data.leader.id)
    .set({
      ...data,
    });
};

export const updateFollowing = (userId: string, data: any): Promise<void> => {
  if (!userId) {
    throw new Error('No User Id provided to set following');
  }

  return userCollection.doc(userId)
    .collection('following')
    .doc(data.leader.id)
    .set({
      ...data,
    });
};

const deleteFollowing = (userId: string, data: any): Promise<void> => {
  if (!userId) {
    throw new Error('No User Id provided to set following');
  }

  return userCollection.doc(userId)
    .collection('following')
    .doc(data.leader.id)
    .delete();
};

export const removeFollowing = async (userId: string, data: any): Promise<void> => {
  if (!userId) {
    throw new Error('No User Id provided to set following');
  }

  return data.groups.length ? updateFollowing(userId, data) : deleteFollowing(userId, data);
};

export const getFollowing = async (userId: string): Promise<any> => {

  const documents = await userCollection
    .doc(userId)
    .collection('following')
    .orderBy('leader.displayName')
    .get();
  return Promise.resolve(documents);
};

export const getDataForLeaders =
  async (leaders: firebase. firestore. QuerySnapshot<firebase. firestore. DocumentData>) => {
    const docPromiseArray: Promise<any>[] = [];
    leaders.forEach(async (leader) => {
      docPromiseArray.push(
        new Promise<any>((resolve) => {
          const followingDocumentData = leader.data();
          userCollection.doc(leader.id).get().then((leaderEntry) => {
            const leaderData = leaderEntry.data();
            followingDocumentData.leader.displayName = leaderData?.displayName;
            followingDocumentData.leader.photoUrl = leaderData?.photoUrl;
            resolve({ ...followingDocumentData });
          });
        }),
      );
    });
    const docsArray: any[] = await Promise.all(docPromiseArray);
    return Promise.resolve({
      following: docsArray,
      lastItem: leaders.docs[leaders.docs.length - 1],
    });
  };
